import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import Button from "@components/Button/Button";
import FormItem from "@components/FormItem/FormItem";
import { useNotification } from "@context/NotificationContext";
import { usePersistentForm } from "@hooks/usePersistentForm";
import type { FinalDraftSurveyRequest } from "@services/drybulkCargoOperationsApi";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FINAL_DRAFT_SURVEY_FORM_PERSIST_KEY } from "../constants";
import { useFinalDraftSurvey } from "../hooks/useFinalDraftSurvey";
import { useFinalDraftSurveyFormStore } from "../hooks/useFinalDraftSurveyFormStore";
import { useHeldIntermediateSurveys } from "../hooks/useHeldIntermediateSurveysStore";
import { useValidationToast } from "../hooks/useValidationToast";
import { sumOfAmounts } from "../utils/closeOutFormat";
import { problemMessage } from "../utils/problemDetail";
import {
  FINAL_DRAFT_SURVEY_FIELDS as FIELDS,
  finalDraftSurveyDefaultValues,
  finalDraftSurveySchema,
  L_FDS,
  type FinalDraftSurveyFormData,
} from "./closeOutSchemas";
import FinalDraftSurveyConfirmDialog from "./FinalDraftSurveyConfirmDialog";

const FinalDraftSurveyEntry: React.FC<{ operationId: string; shipperCodes: string[] }> = ({
  operationId,
  shipperCodes,
}) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const onInvalid = useValidationToast();
  const mutation = useFinalDraftSurvey(operationId);
  const held = useHeldIntermediateSurveys(operationId);
  const storedOperationId = useFinalDraftSurveyFormStore((state) => state.operationId);
  const storedTotal = useFinalDraftSurveyFormStore((state) => state.total);
  const setStoredValues = useFinalDraftSurveyFormStore((state) => state.setValues);
  const resetStore = useFinalDraftSurveyFormStore((state) => state.reset);
  const schema = useMemo(() => finalDraftSurveySchema(t), [t]);
  const storeValues = useMemo(
    () => (storedOperationId === operationId ? { total: storedTotal } : {}),
    [storedOperationId, storedTotal, operationId],
  );
  const form = usePersistentForm<FinalDraftSurveyFormData>({
    schema,
    defaultValues: finalDraftSurveyDefaultValues,
    storeValues,
    setStoreValues: (data) => setStoredValues(operationId, data.total ?? ""),
    resetStore,
    persistKey: FINAL_DRAFT_SURVEY_FORM_PERSIST_KEY,
  });
  const { setValue, getValues } = form;
  const [confirming, setConfirming] = useState<string | null>(null);

  const heldCodes = Object.keys(held.amounts);
  const intermediateMode = heldCodes.length > 0;
  const everyShipperHeld =
    shipperCodes.length > 0 && shipperCodes.every((code) => code in held.amounts);
  const heldSum = sumOfAmounts(Object.values(held.amounts));
  const wasIntermediateMode = useRef(false);
  const typedTotal = useRef("");

  useEffect(() => {
    if (storedOperationId !== null && storedOperationId !== operationId) {
      resetStore();
    }
  }, [storedOperationId, operationId, resetStore]);

  useEffect(() => {
    if (intermediateMode) {
      if (!wasIntermediateMode.current) {
        typedTotal.current = getValues(FIELDS.TOTAL);
      }
      setValue(FIELDS.TOTAL, heldSum);
    } else if (wasIntermediateMode.current) {
      setValue(FIELDS.TOTAL, typedTotal.current);
    }
    wasIntermediateMode.current = intermediateMode;
  }, [intermediateMode, heldSum, setValue, getValues]);

  const buildRequest = (total: string): FinalDraftSurveyRequest =>
    intermediateMode
      ? {
          mode: "PER_SHIPPER",
          intermediaryAmounts: shipperCodes.map((code) => ({
            shipperCompanyCode: code,
            amount: held.amounts[code],
          })),
        }
      : { mode: "TOTAL", total: Number(total) };

  const send = async () => {
    if (confirming === null) {
      return;
    }
    try {
      await mutation.mutateAsync(buildRequest(confirming));
      held.reset();
      form.clear();
    } catch (error) {
      notify({
        type: "error",
        title: t("common.error"),
        description: problemMessage(error) ?? t(`${L_FDS}.failed`),
      });
    } finally {
      setConfirming(null);
    }
  };

  return (
    <FormProvider {...form}>
      <Box
        data-testid="final-draft-survey-entry"
        sx={{ display: "flex", alignItems: "flex-end", gap: 2, flexWrap: "wrap" }}
      >
        <Box sx={{ minWidth: 260 }}>
          <FormItem<FinalDraftSurveyFormData, "total">
            required
            name={FIELDS.TOTAL}
            type="text"
            label={t(`${L_FDS}.label`)}
            testId="final-draft-survey-total"
            disabled={intermediateMode}
            slotProps={{ htmlInput: { inputMode: "decimal" } }}
            onFieldChange={(_, value) => setStoredValues(operationId, String(value ?? ""))}
          />
        </Box>
        <Box sx={{ mb: "28px" }}>
          <Button
            variant="contained"
            testId="final-draft-survey-add"
            disabled={intermediateMode && !everyShipperHeld}
            onClick={() =>
              void form.handleSubmit((values) => setConfirming(values.total), onInvalid)()
            }
            label={<span className="normal-case font-medium">{t(`${L_FDS}.add`)}</span>}
          />
        </Box>
      </Box>
      <FinalDraftSurveyConfirmDialog
        open={confirming !== null}
        total={confirming ?? ""}
        isSubmitting={mutation.isPending}
        onConfirm={() => void send()}
        onCancel={() => setConfirming(null)}
      />
    </FormProvider>
  );
};

export default FinalDraftSurveyEntry;
